import { PAYMENT_COMP, PAYMENT_BILLABLE, PAYMENT_UNIT } from '../../shop/constants';
import { ADDRESS_TYPES } from '../../addressBook/constants';

/**
 * Place product groups here.
 * labelId (function or string) - Gets passed the product group.
 *   Determine label id string based on group or set as a string.
 * groupBy (function) - Gets passed the item. Should return true if the item should belong to the group.
 *  Arguments: cartProduct
 * addressFilterBy (function) - Used to determine which addresses should be applied to each group.
 *  Arguments: address, cartProducts, productGroup
 * canAddAddresses (boolean) - Used to show or hide the Add Addresses button in the address select modal
 */
export const PRODUCT_GROUPS = {
  NORMAL: {
    labelId: cartProducts =>
      cartProducts.some(item => item.isDirectShip)
        ? 'checkout.group.NORMAL_WITH_DS.label'
        : 'checkout.group.NORMAL.label',
    subtitleId: '',
    groupBy: item => !item.isDirectShip,
    addressFilterBy: (address, cartProducts) => {
      const nonDSItems = cartProducts.filter(item => !item.isDirectShip);
      const nonDSItemsAreComp = nonDSItems.every(item => [PAYMENT_COMP, PAYMENT_UNIT].includes(item.paymentMethod));
      const nonDSItemsAreBillable = nonDSItems.every(item => item.paymentMethod === PAYMENT_BILLABLE);

      if (nonDSItemsAreComp) {
        return address.isCustom || address.isComp;
      }

      if (nonDSItemsAreBillable) {
        return address.isCustom || address.isBillable;
      }

      return address.isCustom;
    },
    canAddAddresses: true
  },
  DS_COMP: {
    labelId: 'checkout.group.DS_COMP.label',
    subtitleId: 'checkout.group.DS.subtitle',
    groupBy: item => item.isDirectShip && (item.paymentMethod === PAYMENT_COMP || item.paymentMethod === PAYMENT_UNIT),
    addressFilterBy: address => address.isComp,
    canAddAddresses: false
  },
  DS_BILLABLE: {
    labelId: 'checkout.group.DS_BILLABLE.label',
    subtitleId: 'checkout.group.DS.subtitle',
    groupBy: item => item.isDirectShip && item.paymentMethod === PAYMENT_BILLABLE,
    addressFilterBy: address => address.isBillable,
    canAddAddresses: false
  }
};

export const DEFAULT_CART_PRODUCT_GROUP_STRUCTURE = {
  type: '',
  label: '',
  address: null,
  cartProductIds: [],
  isOpen: true
};

export const DEFAULT_CART_PRODUCT_STRUCTURE = {
  addressId: null,
  addressType: null,
  errors: [],
  fundamentalRuleId: null,
  id: '',
  isDeleting: false,
  isDuplicating: false,
  isPurchasable: false,
  isSelected: false,
  paymentMethod: 'COMP',
  productCode: '',
  retailPrice: 0,
  rules: [],
  sizes: [],
  totals: { retail: 0, unitCount: 0, wholesale: 0 },
  unitAllotmentId: '',
  unitAllotments: [],
  wholesalePrice: 0
};

export const CART_SORT_OPTIONS = [
  { value: 'createdAt', name: 'Recently Added' }, // TODO: API no longer sending?
  { value: ['copy', 'name'], name: 'Alphabetical' },
  { value: ['prices', 'wholesale'], name: 'Wholesale Price', isNumber: true },
  { value: ['totals', 'unitCount'], name: 'Quantity', isNumber: true },
  { value: 'category', name: 'Catalog' },
  { value: 'paymentMethod', name: 'Payment Method' }
];

export const DEFAULT_CART_STRUCTURE = {
  id: '',
  headers: {
    languageCode: 'en', // TODO: This should be pulled from the cart?
    orderType: '',
    cartType: '',
    departmentNumber: '',
    phoneNumber: '',
    phoneNumberExt: '',
    purchaseOrderNumber: '',
    shippingMethod: '',
    notesToNike: '',
    salesOrg: '',
    isBillableOrder: false
  },
  totals: {
    billable: 0,
    comp: 0,
    retail: 0,
    wholesale: 0,
    unitCount: 0,
    productCount: 0,
    unitAllotments: {}
  },
  hasFetchedProducts: false,
  products: [],
  productGroups: Object.keys(PRODUCT_GROUPS).map(type => ({
    ...DEFAULT_CART_PRODUCT_GROUP_STRUCTURE,
    type
  })),
  etag: null,
  errors: [],
  sortBy: CART_SORT_OPTIONS[0],
  status: '',
  searchBy: '',
  createdBy: '',
  productsMaxAmountsOrderable: {}
};

export const DEFAULT_ADDRESS_STRUCTURE = {
  id: '',
  shipToId: '',
  name: '',
  attention: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  shopSuiteFloor: '',
  districtName: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
  isComp: false,
  isBillable: false,
  isLocked: true,
  isActive: true,
  isCustom: false,
  type: ADDRESS_TYPES.SAP
};

export const ADDRESS_SORT_OPTIONS = [
  { value: 'name', name: 'Name' },
  { value: 'attention', name: 'Attention' },
  { value: 'addressLine1', name: 'Address' },
  { value: 'city', name: 'City' },
  { value: 'state', name: 'State' }
];
